// ============================================================================
// device.ts — detecção de dispositivo, ZERO dependências Angular.
// ============================================================================
// Funções puras que usam apenas APIs nativas do navegador (`matchMedia`,
// `navigator`, `screen`). Consumidoras: qualquer componente/serviço via
// `import { isTouch, isPortrait, getDeviceClass } from 'src/app/lib/device';`
//
// Reatividade (Observable) NÃO vive aqui — quando um componente precisa
// re-renderizar ao girar/plugar teclado, envolve as funções em RxJS via
// `DeviceService` (thin wrapper Angular). Assim `lib/device.ts` serve
// callers reativos E não-reativos sem duplicar lógica.
//
// APIs usadas:
//   • CSS Media Level 4 (`pointer`, `hover`, `orientation`) — suporte
//     amplo desde ~2015 (Chrome, Safari, Firefox, Edge).
//   • `navigator.userAgentData` (UA Client Hints) — Chromium apenas;
//     usado só como sinal auxiliar, sempre com fallback.
//   • `screen.width` / `screen.height` — pixels CSS do dispositivo
//     (não do viewport), imutável enquanto o user tá no mesmo aparelho.
// ============================================================================

/** Categoria composta pra ligar variantes de layout no CSS. Combina
 *  classe de dispositivo (mobile/tablet/desktop) com orientação. */
export type DeviceClass =
  | 'mobile-portrait'
  | 'mobile-landscape'
  | 'tablet-portrait'
  | 'tablet-landscape'
  | 'desktop';

/** Instantâneo do estado — útil pra debug ou pra passar como valor
 *  imutável pra funções puras (renderers, formatadores, etc). */
export interface DeviceSnapshot {
  touch: boolean;
  hover: boolean;
  portrait: boolean;
  mobile: boolean;
  deviceClass: DeviceClass;
  /** Diagonal física estimada em polegadas (só existe se detectável). */
  diagonalInches: number | null;
}

// ─── Media Query cache ──────────────────────────────────────────────────────
// `matchMedia()` retorna sempre a MESMA instância pra uma query dada, mas
// cacheamos aqui só pra clareza + garantir que listeners não sejam
// duplicados quando `onDeviceChange` é chamado várias vezes.
const MQ_COARSE_POINTER = window.matchMedia('(pointer: coarse)');
const MQ_HOVER = window.matchMedia('(hover: hover)');
const MQ_PORTRAIT = window.matchMedia('(orientation: portrait)');

/** Ponteiro principal é grosso (dedo) — proxy pra touch device. Mais
 *  confiável que `'ontouchstart' in window` (Safari mac responde true
 *  por causa do trackpad). */
export function isTouch(): boolean {
  return MQ_COARSE_POINTER.matches;
}

/** Dispositivo consegue fazer HOVER real (mouse ou trackpad de precisão).
 *  `false` em touch puro — útil pra desligar tooltip-on-hover. */
export function hasHover(): boolean {
  return MQ_HOVER.matches;
}

/** Orientação atual do dispositivo. Muda ao girar. */
export function isPortrait(): boolean {
  return MQ_PORTRAIT.matches;
}

export function isLandscape(): boolean {
  return !isPortrait();
}

/** Diagonal física ESTIMADA em polegadas do dispositivo. Combina
 *  `screen.width/height` (pixels CSS reais) com `devicePixelRatio`
 *  e assume ~96 DPI base do CSS. Retorna `null` se algum sinal falta. */
export function getDiagonalInches(): number | null {
  if (!screen?.width || !screen?.height) { return null; }
  const w = screen.width;
  const h = screen.height;
  // 96 CSS pixels ≈ 1 inch pelo spec CSS. `devicePixelRatio` corrige
  // pra tamanho físico aparente do device (retina reporta pixels CSS,
  // não físicos — a normalização aqui bate melhor no tamanho real).
  const diagPx = Math.hypot(w, h);
  return diagPx / 96;
}

/** True se for provavelmente um dispositivo MÓVEL (celular),
 *  distinguindo de tablets. Combina 2 sinais em ordem de confiança:
 *    1. `navigator.userAgentData.mobile` (Chromium) — resposta explícita.
 *    2. `Math.min(screen.width, screen.height) < 600` — dimensão
 *       menor do device (CSS px). Phones ficam em ~390-430; tablets
 *       em 768+. Independente de orientação.
 *
 *  NÃO usa largura do viewport do BROWSER (o usuário pode
 *  redimensionar a janela). Só `screen.width/height` (imutável enquanto
 *  o user tá no mesmo aparelho).
 *
 *  A antiga heurística de "diagonal em polegadas via /96" foi removida
 *  — assumia 1x DPR (CRT antigo) e classificava iPhone (DPR 2/3) como
 *  tablet. `screen.width` em CSS px já é normalizado pelo browser. */
export function isMobile(): boolean {
  // Sinal PRIMÁRIO: touch + menor dimensão do device < 600px CSS.
  // Cobre iPhone Pro Max (430), Galaxy Note (412) → mobile.
  // iPad Mini (768) e maiores → tablet.
  const shortSide = Math.min(screen.width, screen.height);
  if (isTouch() && shortSide < 600) { return true; }

  // Sinal AUXILIAR (só POSITIVO): `navigator.userAgentData.mobile`
  // do Chromium promove casos borderline (phablet grande que
  // exceda 600 mas o UA ainda declare mobile). NÃO usamos esse
  // sinal como negativo porque o Chrome DevTools o retorna
  // incorretamente como `false` em emuladores de iPhone.
  const uad = (navigator as any).userAgentData;
  if (uad && uad.mobile === true) { return true; }

  return false;
}

/** True se for provavelmente um TABLET. Toque presente mas não é phone. */
export function isTablet(): boolean {
  return isTouch() && !isMobile();
}

/** True se for desktop/laptop com mouse. */
export function isDesktop(): boolean {
  return !isTouch() && hasHover();
}

/** Categoria composta pra usar como classe CSS (e.g., no `<html>`). */
export function getDeviceClass(): DeviceClass {
  if (isDesktop()) { return 'desktop'; }
  const orient = isPortrait() ? 'portrait' : 'landscape';
  return isMobile() ? `mobile-${orient}` as DeviceClass
                    : `tablet-${orient}` as DeviceClass;
}

/** Snapshot completo do estado — útil pra debug ou logging. */
export function getDeviceSnapshot(): DeviceSnapshot {
  return {
    touch: isTouch(),
    hover: hasHover(),
    portrait: isPortrait(),
    mobile: isMobile(),
    deviceClass: getDeviceClass(),
    diagonalInches: getDiagonalInches(),
  };
}

// ─── Reatividade (sem RxJS) ─────────────────────────────────────────────────

/** Registra `callback` pra ser chamado toda vez que qualquer sinal
 *  observável mudar (orientação, pointer, hover). Retorna função de
 *  cleanup — chamar pra remover os listeners.
 *
 *  Uso típico:
 *    const off = onDeviceChange(snap => document.documentElement
 *                                          .dataset.device = snap.deviceClass);
 *    // ... depois ...
 *    off();  // limpa
 *
 *  Se um componente Angular usar, chame o cleanup no `ngOnDestroy`. */
export function onDeviceChange(callback: (snap: DeviceSnapshot) => void): () => void {
  const handler = () => callback(getDeviceSnapshot());
  const queries = [MQ_COARSE_POINTER, MQ_HOVER, MQ_PORTRAIT];
  queries.forEach(q => q.addEventListener('change', handler));
  return () => queries.forEach(q => q.removeEventListener('change', handler));
}

/** Aplica a classe do device como `data-device` no `<html>`, pra CSS
 *  hooks tipo `html[data-device="mobile-portrait"] .foo { ... }`.
 *  Chame uma vez no bootstrap; segue reagindo a orientation/pointer. */
export function bindDeviceClassToRoot(): () => void {
  const apply = (snap: DeviceSnapshot) => {
    document.documentElement.dataset.device = snap.deviceClass;
    document.documentElement.dataset.deviceTouch = String(snap.touch);
    document.documentElement.dataset.deviceOrientation = snap.portrait ? 'portrait' : 'landscape';
  };
  apply(getDeviceSnapshot());
  return onDeviceChange(apply);
}

/** Detecta o SO (mac/windows/linux/other) e aplica `data-os` no `<html>`
 *  pra CSS hooks tipo `html:not([data-os="mac"]) .foo { ... }`. Usado
 *  hoje pra respeitar convenções nativas de menu — macOS abre menus
 *  instantâneos; Windows/Linux tradicionalmente com animação sutil.
 *  Roda uma vez; o SO não muda em runtime. iPadOS reporta "MacIntel"
 *  no `navigator.platform`, então cruzamos com `maxTouchPoints` pra
 *  distinguir (iPad é tratado como "other"). */
export function bindOsClassToRoot(): void {
  const uaData: any = (navigator as any).userAgentData;
  const platform: string = (uaData && uaData.platform) || navigator.platform || '';
  const isTouchMac = /Mac/i.test(platform) && (navigator.maxTouchPoints || 0) > 1;
  const os = isTouchMac                 ? 'other'
           : /Mac/i.test(platform)      ? 'mac'
           : /Win/i.test(platform)      ? 'windows'
           : /Linux|X11/i.test(platform) ? 'linux'
           :                              'other';
  document.documentElement.dataset.os = os;
}

/** Detecta o navegador principal (chrome/firefox/safari/edge/other) e
 *  aplica `data-browser` no `<html>` pra CSS hooks tipo
 *  `html[data-browser="safari"] .foo { ... }`. Nenhum consumidor ativo
 *  no momento — o único uso histórico (reforço de borda superior do
 *  navbar/breadcrumb/admin-sidebar no Safari legado) foi retirado no
 *  macOS 27, que agora desenha a separação viewport↔canvas nativa e
 *  transformava o reforço em linha dupla. Helper mantido dormente pra
 *  reativação rápida se aparecer novo quirk específico de browser. */
export function bindBrowserClassToRoot(): void {
  const uaData: any = (navigator as any).userAgentData;
  const ua = navigator.userAgent || '';
  const brands: Array<{ brand: string }> = (uaData && uaData.brands) || [];

  // Edge (Chromium) precisa vir ANTES de Chrome — o UA do Edge também
  // contém "Chrome". `userAgentData.brands` distingue no Chromium moderno.
  const isEdge = brands.some(b => /Edge/i.test(b.brand)) || /\bEdg\//.test(ua);
  const isChrome = !isEdge && (
    brands.some(b => /Google Chrome|Chromium/i.test(b.brand)) ||
    (/Chrome\//.test(ua) && !/Edg\/|OPR\//.test(ua))
  );
  const isFirefox = /Firefox/i.test(ua);
  // Safari desktop: UA tem "Safari" mas não "Chrome"/"Chromium". iOS
  // WebKit (todos os browsers no iPad/iPhone) também casa — o que é
  // desejado, já que compartilham o engine e o bug de separação visual.
  const isSafari = !isChrome && !isEdge && /Safari/i.test(ua) && !/Chrome|Chromium|Edg\//i.test(ua);

  const browser = isEdge    ? 'edge'
                : isChrome  ? 'chrome'
                : isFirefox ? 'firefox'
                : isSafari  ? 'safari'
                :             'other';
  document.documentElement.dataset.browser = browser;
}
